'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag';
import { ChevronDown, X } from 'lucide-react';

import type { ActiveFilterData, DataTableActiveFiltersSlotProps } from '../../types/toolbar-slot-props';
import { formatFilterValue } from '../../utils/format-filter-value';

export type DataTableActiveFiltersProps = DataTableActiveFiltersSlotProps;

export function DataTableActiveFilters({
  filters,
  onRemoveFilter,
  onRemoveValue,
  onClearAll,
  maxVisibleValues = 2,
  className,
}: DataTableActiveFiltersProps) {
  if (filters.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {filters.map((filter) => (
        <FilterGroup
          key={filter.columnId}
          filter={filter}
          maxVisibleValues={maxVisibleValues}
          onRemoveFilter={() => onRemoveFilter(filter.columnId)}
          onRemoveValue={(value) => onRemoveValue(filter.columnId, value)}
        />
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
        Clear all
      </button>
    </div>
  );
}

type FilterGroupProps = {
  readonly filter: ActiveFilterData;
  readonly maxVisibleValues: number;
  readonly onRemoveFilter: () => void;
  readonly onRemoveValue: (value: string | number) => void;
};

function FilterGroup({ filter, maxVisibleValues, onRemoveFilter, onRemoveValue }: FilterGroupProps) {
  const { label, filterType, filterMeta, value, options } = filter;
  const isTagType = filterType === 'tag';

  if ('values' in value && Array.isArray(value.values)) {
    const visibleValues = value.values.slice(0, maxVisibleValues);
    const overflowValues = value.values.slice(maxVisibleValues);

    return (
      <div className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-input bg-background px-3">
        <span className="text-sm text-muted-foreground">{label}</span>

        {visibleValues.map((v) => {
          const option = options.find((opt) => opt.value === v);
          const displayLabel = option?.label ?? String(v);
          const color = option?.color;

          return (
            <Tag
              key={String(v)}
              dismissible
              onDismiss={() => onRemoveValue(v)}
              variant="filter"
              color={isTagType ? color : undefined}
            >
              {displayLabel}
            </Tag>
          );
        })}

        {overflowValues.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-sm text-muted-foreground hover:text-foreground"
                  aria-label={`Show ${overflowValues.length} more`}
                />
              }
            >
              +{overflowValues.length}
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {overflowValues.map((v) => {
                const option = options.find((opt) => opt.value === v);
                const displayLabel = option?.label ?? String(v);
                const color = option?.color;

                return (
                  <DropdownMenuItem
                    key={String(v)}
                    className="gap-2"
                    onClick={() => {
                      onRemoveValue(v);
                    }}
                  >
                    {isTagType && color && (
                      <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
                    )}
                    <span className="flex-1">{displayLabel}</span>
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  const displayValue = formatFilterValue(filterType, filterMeta, options, value);

  return (
    <div className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-input bg-background px-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Tag dismissible onDismiss={onRemoveFilter} variant="filter">
        {displayValue}
      </Tag>
    </div>
  );
}
